import { PublicBrandingTheme } from './brandingThemes';
import {
  Money,
  Status,
  PaginatedResponse,
  DateTimeISO,
  StatusQueryParam,
} from './common';

export type ShortCompany = {
  uuid: string;
  name: string;
  rateSavingInterest: string;
};

export type BaseCompany = {
  uuid: string;
  name: string;
  rateSavingInterest: number;
  teammatePerms?: number[];
  feeMonthlySavingAccount: Money | null;
  feeIncomingDomestic: Money | null;
  feeOutcomeDomestic: Money | null;
};

export type CompanyAggregateNumbers = {
  teammates: number;
  customers: number;
  businesses: number;
  bank_accounts: number;
  draftBankApplications: number;
};

export type CompanyListItem = {
  uuid: string;
  name: string;
  status: Status;
  aggregateNumbers: CompanyAggregateNumbers;
};

export type CompanyListResponse = {
  companies: PaginatedResponse<CompanyListItem>;
  aggregateInfo: {
    totalActiveTeammates: number;
    totalActiveConsumers: number;
    totalActiveBusinesses: number;
    totalActiveBankAccounts: number;
  };
};

export type CompanyDetails = {
  uuid: string;
  name: string;
  alias: string;
  companyUrl: string;
  advisorOfficeType: 0;
  status: 1;
  rateSavingInterest: string;
  rateSavingInterestUpdatedAt: DateTimeISO;
  relationshipManager: {
    uuid: string;
    name: string;
    email: string;
  };
  lendingEnabled: boolean;
  createdAt: DateTimeISO;
  teammatePerms: number[];
  minimumInitDeposit?: Money;
  maximumInitDeposit?: Money;
  feeIncomingDomestic?: Money;
  feeOutcomeDomestic?: Money;
  feeMonthlySavingAccount?: Money;
  brandingTheme: {
    uuid: string;
    name: string;
    level: 'All' | 'Program' | 'Company';
  };
  aggNumbers: {
    divisions: number;
    teammates: number;
    customers: number;
    businesses: number;
    bankAccounts: number;
    draftBankApplications: number;
    totalAvailableBalance: Money;
  };
  deactivatedDate: DateTimeISO;
  reactivatedDate: DateTimeISO;
  advisorCommissionRate: string;
  advisorCommissionRateUpdatedAt: DateTimeISO;
  internalCommissionRate: string;
  internalCommissionRateUpdatedAt: DateTimeISO;
};

export type CompanyForSelectItem = {
  id: string;
  uuid: string;
  name: string;
};

export type PublicCompany = {
  uuid: string;
  name: string;
  companyLoginUrl: string;
  brandingTheme: PublicBrandingTheme;
  rateSavingInterest: number;
  debitCardProgramId: string | null;
  feeIncomingDomestic: Money | null;
  feeOutcomeDomestic: Money | null;
  feeMonthlySavingAccount: Money | null;
};

export const queryKeys = {
  list: ['companiesList'] as const,
  selectionList: ['companiesSelectionList'] as const,
  details: ['companyDetails'] as const,
  preflight: ['companyPreflight'] as const,
};

export type CompanyForSelectionQueryParams = {
  company_id?: string;
  status?: StatusQueryParam;
};

export type UpdateSavingsInterestRateFormFields = {
  rateSavingInterest: number;
};

export enum AdvisorOfficeType {
  RIA = 0, // Registered Investment Advisor
  IBD = 1, // Independent Broker-Dealer
}

export enum PermissionCompanyType {
  TEAMMATE_CREATE = 0,
  TEAMMATE_UPDATE = 1,
  TEAMMATE_DEACTIVATE_REACTIVATE = 2,
  DIVISION_CREATE = 3,
  DIVISION_UPDATE = 4,
  DIVISION_DELETE = 5,
}

export enum Currency {
  USD = 'usd',
}

export type CompanyFormFields = {
  uuid: string;
  name: string;
  brandingThemeId: string;
  lendingEnabled: boolean;
  advisorOfficeType?: AdvisorOfficeType;
  relationshipManagerId: string;
  teammateCreatePerm: boolean;
  teammateUpdatePerm: boolean;
  teammateDeactivateReactivatePerm: boolean;
  divisionCreatePerm: boolean;
  divisionUpdatePerm: boolean;
  divisionDeletePerm: boolean;
  rateSavingInterest?: string;
  minimumInitDeposit?: string;
  alias: string;
  // Fee fields - null = use program setting, value = override
  feeIncomingDomestic?: string;
  feeOutcomeDomestic?: string;
  feeMonthlySavingAccount?: string;
  useProgramIncomingWireFee: boolean;
  useProgramOutgoingWireFee: boolean;
  useProgramMonthlySavingsFee: boolean;
  debitCardProgramId?: string;
};
